import React, { createContext, useState, useContext, useEffect } from 'react'
import { Trade, TradingModel, DailyStats } from '../types'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabaseClient'

interface DayPerformance {
  date: string;
  pnl: number;
  followedPlan: boolean;
}

interface TradeContextType {
  trades: Trade[]
  tradingModels: TradingModel[]
  addTrade: (trade: Omit<Trade, 'id' | 'userId'>) => Promise<void>
  updateTrade: (id: string, trade: Omit<Trade, 'id' | 'userId'>) => Promise<void>
  deleteTrade: (id: string) => Promise<void>
  addTradingModel: (model: Omit<TradingModel, 'id' | 'userId'>) => Promise<void>
  updateTradingModel: (id: string, model: Omit<TradingModel, 'id' | 'userId'>) => Promise<void>
  deleteTradingModel: (id: string) => Promise<void>
  deleteAllTrades: () => Promise<void>
  deleteAllTradingModels: () => Promise<void>
  getDailyPerformance: (date: string) => DayPerformance | undefined
  getMonthlyPerformance: (year: number, month: number) => DayPerformance[]
  isLoading: boolean
  error: string | null
}

const TradeContext = createContext<TradeContextType | undefined>(undefined)

export const TradeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, hasSubscription, isTrial, loading } = useAuth()
  const [trades, setTrades] = useState<Trade[]>([])
  const [tradingModels, setTradingModels] = useState<TradingModel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // DEBUG: Log authentication status
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 TradeContext Auth Check:', {
        hasUser: !!user,
        hasSubscription,
        isTrial,
        loading,
        canAccess: !!(user && (hasSubscription || isTrial)),
        userId: user?.id
      });
    }

    // FIXED: Wait for auth loading to complete before checking access
    if (loading) {
      console.log('⏳ TradeContext: Waiting for auth to complete...');
      return
    }

    // FIXED: Allow both subscription AND trial users
    if (!user || (!hasSubscription && !isTrial)) {
      console.log('❌ TradeContext: Access denied - no user or subscription/trial');
      setTrades([])
      setTradingModels([])
      setError(null)
      return
    }

    // DEBUG: Log successful auth check
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ TradeContext: Auth check passed, fetching data for user:', user.id);
    }

    const fetchModels = async () => {
      setIsLoading(true)
      setError(null)
      const { data, error: e } = await supabase
        .from('trading_models')
        .select('*')
        .eq('user_id', user.id)

      if (e) {
        setError(e.message)
        setTradingModels([])
      } else {
        const recs = (data as any[]) || []
        setTradingModels(
          recs.map(m => ({
            id: m.id,
            name: m.name,
            description: m.description,
            imageUrl: m.image_url || undefined,
            additionalImages: m.additional_images || undefined,
            userId: m.user_id,
          }))
        )
      }
      setIsLoading(false)
    }

    const fetchTrades = async () => {
      setIsLoading(true)
      setError(null)
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 TradeContext: Fetching trades');
      }
      
      const { data, error: e } = await supabase
        .from('trades')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 TradeContext: Database response:', {
          dataCount: data?.length || 0,
          hasError: !!e,
          errorMessage: e?.message
        });
      }

      if (e) {
        console.log('❌ TradeContext: Database error:', e.message);
        setError(e.message)
        setTrades([])
      } else {
        const recs = (data as any[]) || [];
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ TradeContext: Setting', recs.length, 'trades');
          
          // DEBUG: Log first few P&L values to diagnose the issue
          if (recs.length > 0) {
            const samplePnl = recs.slice(0, 10).map(t => ({ 
              id: t.id, 
              pnl: t.pnl, 
              pnlType: typeof t.pnl,
              pnlValue: Number(t.pnl) || 0
            }));
            console.log('🔍 TradeContext: Sample P&L values:', samplePnl);
            
            const totalSamplePnl = recs.slice(0, 10).reduce((sum, t) => sum + (Number(t.pnl) || 0), 0);
            console.log('🔍 TradeContext: Total sample P&L:', totalSamplePnl);
          }
        }
        
        const shouldLogTrades = process.env.NODE_ENV === 'development' && recs.length < 200;
        const batched: { id: string; date: string }[] = [];
        
        setTrades(
          recs.map(t => {
            // Create the basic trade object
            if (shouldLogTrades) {
              batched.push({ id: t.id, date: t.date });
            }
            const trade: Trade = {
              id: t.id,
              date: t.date,
              symbol: t.symbol || undefined,
              tradeType: t.tradetype,
              tradingModelId: t.tradingmodelid || undefined,
              session: t.session || undefined,
              pnl: t.pnl,
              rr: t.rr ?? undefined,
              followedPlan: t.followedplan,
              notes: t.notes || undefined,
              imageUrl: t.imageurl || undefined,
              userId: t.user_id,
            }
            
            // Add extended properties if they exist
            if (t.imageurl2) trade.imageUrl2 = t.imageurl2;
            if (t.outcome) trade.outcome = t.outcome;
            
            // Add advanced trading details if they exist - MAP SNAKE_CASE DB FIELDS TO CAMELCASE
            if (t.entry_time) trade.entryTime = t.entry_time;
            if (t.exit_time) trade.exitTime = t.exit_time;
            if (t.entry_price) trade.entryPrice = t.entry_price;
            if (t.exit_price) trade.exitPrice = t.exit_price;
            if (t.stop_loss) trade.stopLoss = t.stop_loss;
            if (t.handles) trade.handles = t.handles;
            if (t.trade_duration) trade.tradeDuration = t.trade_duration;
            if (t.account) trade.account = t.account;
            if (t.feeling) trade.feeling = t.feeling;
            
            return trade;
          })
        );
        
        if (shouldLogTrades) {
          console.table(batched);
        }
      }
      setIsLoading(false)
    }

    fetchModels()
    fetchTrades()
  }, [user, hasSubscription, isTrial, loading])

  const addTrade = async (tradeData: Omit<Trade, 'id' | 'userId'>) => {
    if (!user) throw new Error('No user logged in')
    
    // Type assertion to access any property
    const anyData = tradeData as any;
    
    // DEBUG: Log date before database insertion
    console.log('📀 TradeContext: Adding trade with date:', tradeData.date);
    console.log('📀 TradeContext: Date type:', typeof tradeData.date);

    // Create the basic payload
    const payload: any = {
      date: tradeData.date,
      symbol: tradeData.symbol ?? null,
      tradetype: tradeData.tradeType,
      tradingmodelid: tradeData.tradingModelId ?? null,
      session: tradeData.session ?? null,
      pnl: tradeData.pnl,
      rr: tradeData.rr ?? null,
      followedplan: tradeData.followedPlan,
      notes: tradeData.notes ?? null,
      imageurl: tradeData.imageUrl ?? null,
      user_id: user.id,
    }
    
    // Add extended properties
    if (anyData.imageUrl2) {
      payload.imageurl2 = anyData.imageUrl2;
    }
    
    if (anyData.outcome) {
      payload.outcome = anyData.outcome;
    }
    
    // CRITICAL FIX: Check for both snake_case (from form) and camelCase (from interface)
    // For entry_time/entryTime
    if (anyData.entry_time) {
      payload.entry_time = anyData.entry_time;
    } else if (anyData.entryTime) {
      payload.entry_time = anyData.entryTime;
    }
    
    // For exit_time/exitTime
    if (anyData.exit_time) {
      payload.exit_time = anyData.exit_time;
    } else if (anyData.exitTime) {
      payload.exit_time = anyData.exitTime;
    }
    
    // For entry_price/entryPrice
    if (anyData.entry_price) {
      payload.entry_price = anyData.entry_price;
    } else if (anyData.entryPrice) {
      payload.entry_price = anyData.entryPrice;
    }
    
    // For exit_price/exitPrice
    if (anyData.exit_price) {
      payload.exit_price = anyData.exit_price;
    } else if (anyData.exitPrice) {
      payload.exit_price = anyData.exitPrice;
    }
    
    // For stop_loss/stopLoss
    if (anyData.stop_loss) {
      payload.stop_loss = anyData.stop_loss;
    } else if (anyData.stopLoss) {
      payload.stop_loss = anyData.stopLoss;
    }
    
    // For handles
    if (anyData.handles) {
      payload.handles = anyData.handles;
    }
    
    // For trade_duration/tradeDuration
    if (anyData.trade_duration) {
      payload.trade_duration = anyData.trade_duration;
    } else if (anyData.tradeDuration) {
      payload.trade_duration = anyData.tradeDuration;
    }
    
    // For account
    if (anyData.account) {
      payload.account = anyData.account;
    }
    
    // For feeling
    if (anyData.feeling) {
      payload.feeling = anyData.feeling;
    }
    
    console.log('FINAL PAYLOAD TO DATABASE:', payload);
    
    const { data, error: e } = await supabase
      .from('trades')
      .insert([payload])
      .select()
      .single()

    if (e || !data) throw e || new Error('Insert failed')
    const t = data as any

    // Create the basic trade object for state update
    const newTrade: Trade = {
      id: t.id,
      date: t.date,
      symbol: t.symbol || undefined,
      tradeType: t.tradetype,
      tradingModelId: t.tradingmodelid || undefined,
      session: t.session || undefined,
      pnl: t.pnl,
      rr: t.rr ?? undefined,
      followedPlan: t.followedplan,
      notes: t.notes || undefined,
      imageUrl: t.imageurl || undefined,
      userId: t.user_id,
    }
    
    // Add extended properties from response
    if (t.imageurl2) {
      newTrade.imageUrl2 = t.imageurl2;
    }
    
    if (t.outcome) {
      newTrade.outcome = t.outcome;
    }
    
    // Add advanced trading details from response 
    if (t.entry_time) newTrade.entryTime = t.entry_time;
    if (t.exit_time) newTrade.exitTime = t.exit_time;
    if (t.entry_price) newTrade.entryPrice = t.entry_price;
    if (t.exit_price) newTrade.exitPrice = t.exit_price;
    if (t.stop_loss) newTrade.stopLoss = t.stop_loss;
    if (t.handles) newTrade.handles = t.handles;
    if (t.trade_duration) newTrade.tradeDuration = t.trade_duration;
    if (t.account) newTrade.account = t.account;
    if (t.feeling) newTrade.feeling = t.feeling;

    setTrades(prev => [newTrade, ...prev])
  }

  const updateTrade = async (id: string, tradeData: Omit<Trade, 'id' | 'userId'>) => {
    if (!user) return
    
    // Type assertion to access any property
    const anyData = tradeData as any;
    
    // Create the basic payload
    const payload: any = {
      date: tradeData.date,
      symbol: tradeData.symbol ?? null,
      tradetype: tradeData.tradeType,
      tradingmodelid: tradeData.tradingModelId ?? null,
      session: tradeData.session ?? null,
      pnl: tradeData.pnl,
      rr: tradeData.rr ?? null,
      followedplan: tradeData.followedPlan,
      notes: tradeData.notes ?? null,
      imageurl: tradeData.imageUrl ?? null,
    }
    
    // Add extended properties
    if (anyData.imageUrl2) {
      payload.imageurl2 = anyData.imageUrl2;
    } else {
      // If imageUrl2 is not provided, explicitly set it to null
      // This ensures any existing second image is removed when updating
      payload.imageurl2 = null;
    }
    
    if (anyData.outcome) {
      payload.outcome = anyData.outcome;
    } else {
      payload.outcome = null;
    }
    
    // CRITICAL FIX: Check for both snake_case (from form) and camelCase (from interface)
    // Default all to null first
    payload.entry_time = null;
    payload.exit_time = null;
    payload.entry_price = null;
    payload.exit_price = null;
    payload.stop_loss = null;
    payload.handles = null;
    payload.trade_duration = null;
    payload.account = null;
    payload.feeling = null;
    
    // For entry_time/entryTime
    if (anyData.entry_time) {
      payload.entry_time = anyData.entry_time;
    } else if (anyData.entryTime) {
      payload.entry_time = anyData.entryTime;
    }
    
    // For exit_time/exitTime
    if (anyData.exit_time) {
      payload.exit_time = anyData.exit_time;
    } else if (anyData.exitTime) {
      payload.exit_time = anyData.exitTime;
    }
    
    // For entry_price/entryPrice
    if (anyData.entry_price) {
      payload.entry_price = anyData.entry_price;
    } else if (anyData.entryPrice) {
      payload.entry_price = anyData.entryPrice;
    }
    
    // For exit_price/exitPrice
    if (anyData.exit_price) {
      payload.exit_price = anyData.exit_price;
    } else if (anyData.exitPrice) {
      payload.exit_price = anyData.exitPrice;
    }
    
    // For stop_loss/stopLoss
    if (anyData.stop_loss) {
      payload.stop_loss = anyData.stop_loss;
    } else if (anyData.stopLoss) {
      payload.stop_loss = anyData.stopLoss;
    }
    
    // For handles
    if (anyData.handles) {
      payload.handles = anyData.handles;
    }
    
    // For trade_duration/tradeDuration
    if (anyData.trade_duration) {
      payload.trade_duration = anyData.trade_duration;
    } else if (anyData.tradeDuration) {
      payload.trade_duration = anyData.tradeDuration;
    }
    
    // For account
    if (anyData.account) {
      payload.account = anyData.account;
    }
    
    // For feeling
    if (anyData.feeling) {
      payload.feeling = anyData.feeling;
    }
    
    console.log('FINAL UPDATE PAYLOAD TO DATABASE:', payload);
    
    const { data, error: e } = await supabase
      .from('trades')
      .update(payload)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single()

    if (e || !data) throw e || new Error('Update failed')
    const t = data as any

    // Create the basic trade object for state update
    const updatedTrade: Trade = {
      id: t.id,
      date: t.date,
      symbol: t.symbol || undefined,
      tradeType: t.tradetype,
      tradingModelId: t.tradingmodelid || undefined,
      session: t.session || undefined,
      pnl: t.pnl,
      rr: t.rr ?? undefined,
      followedPlan: t.followedplan,
      notes: t.notes || undefined,
      imageUrl: t.imageurl || undefined,
      userId: t.user_id,
    }
    
    // Add extended properties from response
    if (t.imageurl2) {
      updatedTrade.imageUrl2 = t.imageurl2;
    }
    
    if (t.outcome) {
      updatedTrade.outcome = t.outcome;
    }
    
    // Add advanced trading details from response
    if (t.entry_time) updatedTrade.entryTime = t.entry_time;
    if (t.exit_time) updatedTrade.exitTime = t.exit_time;
    if (t.entry_price) updatedTrade.entryPrice = t.entry_price;
    if (t.exit_price) updatedTrade.exitPrice = t.exit_price;
    if (t.stop_loss) updatedTrade.stopLoss = t.stop_loss;
    if (t.handles) updatedTrade.handles = t.handles;
    if (t.trade_duration) updatedTrade.tradeDuration = t.trade_duration;
    if (t.account) updatedTrade.account = t.account;
    if (t.feeling) updatedTrade.feeling = t.feeling;

    setTrades(prev =>
      prev.map(x => (x.id === id ? updatedTrade : x))
    )
  }

  const deleteTrade = async (id: string) => {
    if (!user) return
    const { error: e } = await supabase
      .from('trades')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id)
    if (e) throw e
    setTrades(prev => prev.filter(x => x.id !== id))
  }

  const addTradingModel = async (modelData: Omit<TradingModel, 'id' | 'userId'>) => {
    if (!user) return
    const payload = {
      name: modelData.name,
      description: modelData.description,
      image_url: modelData.imageUrl ?? null,
      additional_images: (modelData as any).additionalImages ?? null,
      user_id: user.id,
    }
    const { data, error: e } = await supabase
      .from('trading_models')
      .insert([payload])
      .select()
      .single()

    if (e || !data) throw e || new Error('Insert failed')
    const m = data as any
    setTradingModels(prev => [
      ...prev,
      {
        id: m.id,
        name: m.name,
        description: m.description,
        imageUrl: m.image_url || undefined,
        additionalImages: m.additional_images || undefined,
        userId: m.user_id,
      },
    ])
  }

  const updateTradingModel = async (id: string, modelData: Omit<TradingModel, 'id' | 'userId'>) => {
    if (!user) return
    const payload = {
      name: modelData.name,
      description: modelData.description,
      image_url: modelData.imageUrl ?? null,
      additional_images: (modelData as any).additionalImages ?? null,
    }
    const { data, error: e } = await supabase
      .from('trading_models')
      .update(payload)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single()

    if (e || !data) throw e || new Error('Update failed')
    const m = data as any
    setTradingModels(prev =>
      prev.map(x =>
        x.id === id
          ? {
              id: m.id,
              name: m.name,
              description: m.description,
              imageUrl: m.image_url || undefined,
              additionalImages: m.additional_images || undefined,
              userId: m.user_id,
            } as any
          : x
      )
    )
  }

  const deleteTradingModel = async (id: string) => {
    if (!user) return
    const { error: e } = await supabase
      .from('trading_models')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id)
    if (e) throw e
    setTradingModels(prev => prev.filter(x => x.id !== id))
  }

  const deleteAllTrades = async () => {
    if (!user) return
    const { error: e } = await supabase.from('trades').delete().eq('user_id', user.id)
    if (e) throw e
    setTrades([])
  }

  const deleteAllTradingModels = async () => {
    if (!user) return
    const { error: e } = await supabase.from('trading_models').delete().eq('user_id', user.id)
    if (e) throw e
    setTradingModels([])
  }

  const getDailyPerformance = (date: string): DayPerformance | undefined => {
    const day = trades.filter(t => t.date === date)
    if (!day.length) return undefined
    return {
      date,
      pnl: day.reduce((sum, x) => sum + x.pnl, 0),
      followedPlan: day.every(x => x.followedPlan),
    }
  }

  const getMonthlyPerformance = (year: number, month: number): DayPerformance[] => {    
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const out: DayPerformance[] = []
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`
      const day = trades.filter(t => t.date === ds)
      if (day.length) {
        out.push({
          date: ds,
          pnl: day.reduce((sum, x) => sum + x.pnl, 0),
          followedPlan: day.every(x => x.followedPlan),
        })
      }
    }
    return out
  }

  return (
    <TradeContext.Provider
      value={{
        trades,
        tradingModels,
        addTrade,
        updateTrade,
        deleteTrade,
        addTradingModel,
        updateTradingModel,
        deleteTradingModel,
        deleteAllTrades,
        deleteAllTradingModels,
        getDailyPerformance,
        getMonthlyPerformance,
        isLoading,
        error,
      }}
    >
      {children}
    </TradeContext.Provider>
  )
}

export const useTrade = () => {
  const ctx = useContext(TradeContext)
  if (!ctx) throw new Error('useTrade must be used within TradeProvider')
  return ctx
}